import { Component } from '@angular/core';
import { BatchService } from '../Services/batch.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UserService } from '../Services/user.service';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, Subject, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { PicklistService } from '../Services/picklist.service';
import { Modal } from 'bootstrap';
import { StoreService } from '../Services/store.service';
import { CLOSED_STATUSES, LOCKED_STATUSES, isBatchPick } from '../models/batch-status';
@Component({
  selector: 'app-batch',
  imports: [CommonModule, FormsModule],
  templateUrl: './batch.component.html',
  styleUrl: './batch.component.css',
  providers: [BatchService, UserService, StoreService],
})
export class BatchComponent {
  batches: any[] = [];
  userNameMap: { [key: string]: string } = {};
  searchTerm: string = '';
  // 'Open' is every batch not finished yet, so a batch stays listed through picking and verifying.
  selectedStatus: string = 'Open';
  loading = true;
  summary: any = [];
  batchDetailModalInstance: Modal | null = null;
  stores: any[] = [];
  selectedStoreId: number | null = null;
  // storeSubject = new Subject<number | null>();
  storeSubject = new BehaviorSubject<number | null>(null);
  //pagination
  currentPage: number = 1;
  // A day's batches fit on one page (McLernons sent 14 on 30 Oct). The API sends no total, so a
  // full page means there may be another.
  pageSize: number = 25;
  readonly isBatchPick = isBatchPick;
  private listRequest?: Subscription;

  /** A full page may have another after it. */
  get hasNextPage(): boolean {
    return this.batches.length >= this.pageSize;
  }
  statusSubject = new Subject<string>();
  selectedBatch: any = null;
  isStoreUser = false;
  constructor(
    private _batchservice: BatchService,
    private router: Router,
    private _userservice: UserService,
    private _picklistservice: PicklistService,
    private _storeservice: StoreService,
  ) {}
  ngOnInit() {
    this.listenToFilters();
    this.statusSubject.next(this.selectedStatus);
    const role = this._userservice.checkRole();
    this.isStoreUser = role === 'Admin' || role === 'StoreAdmin' || role === 'StoreUser';
    // if (!this.isStoreUser) {
    //   this.loadStores();
    // }
    if (role === 'Admin') {
      this.loadStores();
    }
  }
  getAllBatch(pageNumber: number = 1) {
    this.loading = true;
    const shownPage = this.currentPage;
    this.currentPage = pageNumber;
    // Only the latest request counts: a slow reply for an older page or filter is dropped.
    this.listRequest?.unsubscribe();
    this.listRequest = this._batchservice
      .getAllBatch(
        pageNumber,
        this.pageSize,
        this.selectedStatus,
        this.searchTerm,
        this.selectedStoreId,
      )
      .subscribe(
        (response: any) => {
          this.batches = response?.result ?? [];
          this.loading = false;
          this.batches.forEach((batch) => this.fetchUserName(batch.userName));
        },
        (error) => {
          if (error.status === 404) {
            this.batches = []; // Show empty list instead of failing
          } else {
            // Keep the page we have, under its own number.
            console.error('Failed to fetch batches:', error);
            this.currentPage = shownPage;
          }
          this.loading = false;
        },
      );
  }
  loadStores() {
    this._storeservice.getStores().subscribe({
      next: (res: any) => {
        console.log('stores api result:', res);
        this.stores = res.result;
      },
      error: () => {
        console.error('Failed to load stores');
      },
    });
  }
  trackByStoreId(index: number, store: any) {
    return store.storeId;
  }

  get filteredBatches() {
    if (!this.searchTerm.trim()) return this.batches;
    const term = this.searchTerm.toLowerCase();
    return this.batches.filter((batch) => {
      const mapped = this.userNameMap[batch.userName];
      if (!mapped) return true;
      const [name, ...rest] = mapped.split(',');
      const email = rest.join(' ');
      return (
        name.toLowerCase().includes(term) || email.toLowerCase().includes(term)
      );
    });
  }
  fetchUserName(userName: string) {
    if (!userName || this.userNameMap[userName]) return;
    this._userservice.getUserById(userName).subscribe((response: any) => {
      console.log('r', response);
      this.userNameMap[userName] =
        `${response.result.name},${response.result.email}`;
      console.log('resp', response);
    });
  }
  getMedicationCount(batch: any): number {
    return batch.prescriptions.reduce(
      (total: number, prescription: any) =>
        total + prescription.medications.length,
      0,
    );
  }
  /** Batches that can still be scanned or picked; finished, refused and cancelled ones can't. */
  isOpen(batch: any): boolean {
    return !CLOSED_STATUSES.includes(batch?.status);
  }

  /** A finished MPS batch can still reprint its bag labels (not a refused or cancelled one). */
  canReprintBags(batch: any): boolean {
    return this.isStoreUser && batch?.source === 'Mps' && !this.isOpen(batch) && !LOCKED_STATUSES.includes(batch?.status);
  }

  /** Opens the screen that reprints a finished batch's bag labels. */
  openBagLabels(batch: any) {
    if (isBatchPick(batch)) {
      this.router.navigate(['/batchpick', batch.batchId, 'verify']);
    } else {
      this.router.navigate(['/batch', batch.batchId], { queryParams: { mode: 'view' } });
    }
  }

  scanBatch(batchId: string, batch?: any) {
    if (this.batchDetailModalInstance) {
      this.batchDetailModalInstance.hide();
      this.batchDetailModalInstance = null;
    }
    const target = batch ?? this.selectedBatch ?? this.batches.find((b) => b.batchId === batchId);
    const isPick = target && isBatchPick(target);
    if (isPick) {
      if (target.status === 'Pending' || target.status === 'Picking') {
        this.router.navigate(['/batchpick', batchId, 'pick']);
        return;
      }
      if (target.status === 'Picked' || target.status === 'Verifying') {
        this.router.navigate(['/batchpick', batchId, 'verify']);
        return;
      }
      // Completed BatchPick falls through to the standard detail route
    }
    this.router.navigate(['/batch', batchId]);
  }
  calculatePendingTime(createdAt: any): string {
    const createdDate = new Date(createdAt);
    const now = new Date();

    const diffMs = now.getTime() - createdDate.getTime();

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    let result = '(Pending from ';
    if (diffDays > 0) result += `${diffDays}d `;
    if (diffHours > 0) result += `${diffHours}h `;
    result += `${diffMinutes}m)`;

    return result;
  }
  onStatusChange(status: string) {
    this.statusSubject.next(status);
  }
  onStoreChange(storeId: number | null) {
    this.storeSubject.next(storeId);
  }
  listenToFilters() {
    combineLatest([
      this.statusSubject.pipe(distinctUntilChanged()),
      this.storeSubject.pipe(distinctUntilChanged()),
    ]).subscribe(([status, storeId]) => {
      this.selectedStatus = status;
      this.selectedStoreId = storeId;
      this.currentPage = 1;
      this.batches = [];
      this.getAllBatch(1);
    });
  }
  getUserDisplay(userName: string): { name: string; email: string } {
    const mapped = this.userNameMap[userName];
    if (!mapped) {
      return { name: 'Loading...', email: '' };
    }
    const [name, ...rest] = mapped.split(',');
    return { name, email: rest.join(' ') };
  }
  //print pick list
  printPickList(batchId: string) {
    // Opened during the click, so pop-up blockers allow it; filled once the batch arrives.
    const popup = this._picklistservice.openWindow();
    if (!popup) {
      alert('The pick list window was blocked. Allow pop-ups for SafeScan and try again.');
      return;
    }
    this._batchservice.getBatchById(batchId).subscribe({
      next: (response: any) => {
        if (response.result) this._picklistservice.write(popup, response.result);
        else popup.close();
      },
      error: () => popup.close(),
    });
  }
  //modal
  openBatchDetailModal(batch: any) {
    this.selectedBatch = batch;
    const modalElement = document.getElementById('batchDetailModal');
    if (modalElement) {
      this.batchSummary(this.selectedBatch);
      console.log('batchde', this.selectedBatch);
      this.batchDetailModalInstance = new Modal(modalElement);
      this.batchDetailModalInstance.show();
    }
  }
  //batch summary
  // batchSummary(batch: any) {
  //   // console.log(batch);

  //   let totalPrescription = 0;
  //   let totalMedicines = 0;
  //   const patientSummaries: any[] = [];

  //   batch.prescriptions.forEach((prescription: any) => {
  //     totalPrescription++;
  //     const patientName = prescription.patientName
  //     const medicationCount = prescription.medications?.length || 0;
  //     totalMedicines += medicationCount;

  //     patientSummaries.push({
  //       patientName,
  //       medicationCount
  //     });
  //   });

  //   this.summary = {
  //     totalPrescription,
  //     totalMedicines,
  //     patientSummaries
  //   };
  // }

  //   batchSummary(batch: any) {
  //   const patientSummaries: any[] = [];

  //   batch.prescriptions.forEach((prescription: any) => {
  //     const medications = prescription.medications.map((med: any) => ({
  //       medicationName: med.medicationName,
  //       dosage: med.dosage,
  //       isAvailable: med.isAvailable
  //     }));

  //     patientSummaries.push({
  //       patientName: prescription.patientName,
  //       medications
  //     });
  //   });

  //   this.summary = {
  //     patientSummaries
  //   };
  // }

  batchSummary(batch: any) {
    const patientSummaries: any[] = [];

    batch.prescriptions.forEach((prescription: any) => {
      patientSummaries.push({
        patientName: prescription.patientName,
        medications: prescription.medications.map((med: any) => ({
          medicationName: med.medicationName,
          dosage: med.dosage,
          isAvailable: med.isAvailable,
        })),
        expanded: false,
      });
    });

    this.summary = {
      patientSummaries,
    };
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      Pending: 'ss-status-pending',
      InProgress: 'ss-status-inprogress',
      Completed: 'ss-status-completed',
      Cancelled: 'ss-status-cancelled',
      PartialComplete: 'ss-status-partial',
    };
    return map[status] ?? '';
  }

  togglePatient(patient: any) {
    patient.expanded = !patient.expanded;
  }
}
